// Copyright 2026 FlairCV Inc.
// SPDX-License-Identifier: LicenseRef-FlairCV-Enterprise

/**
 * Full-screen overlay shown while user data is being restored from cloud.
 *
 * Displayed when a user returns after their data was purged from PostgreSQL.
 * The overlay blocks interaction until hydration completes (or fails).
 *
 * Flow: vault unlocked → needs_hydration=true → show overlay → POST /hydrate → done
 */

import { useEffect } from 'react';
import { CloudDownload, Loader2, AlertCircle, RefreshCw } from 'lucide-react';
import { useHydrateFromCloud, useVaultStatus } from '@/app/features/storage';
import { useAuthStore } from '@/app/stores/auth-store';
import {
  shouldAttemptHydration,
  hydrationFailureKind,
} from './hydration-attempt_enterprise';

export function HydrationOverlay() {
  const { data: vaultStatus } = useVaultStatus();
  const hydrateMutation = useHydrateFromCloud();
  const storageProvider = useAuthStore((s) => s.user?.storageProvider);

  // Only cloud-storage users (gdrive / dropbox) can need hydration.
  // useVaultStatus() is already gated on isCloudStorageUser(), but we
  // add the explicit guard here for clarity and defence-in-depth.
  const isCloudUser = storageProvider === 'gdrive' || storageProvider === 'dropbox';

  const needsHydration =
    isCloudUser &&
    vaultStatus?.unlocked === true &&
    vaultStatus?.data_hydrated === false;

  // Auto-trigger hydration once, then leave it to the user.
  //
  // A failed attempt must NOT re-trigger: it settles with isPending false and
  // isSuccess false, which looks identical to "not started", so the effect
  // re-fired the moment it finished and hammered /storage/hydrate — a cloud
  // download per request — in an unbounded loop. See shouldAttemptHydration.
  useEffect(() => {
    if (
      shouldAttemptHydration({
        needsHydration,
        isPending: hydrateMutation.isPending,
        isSuccess: hydrateMutation.isSuccess,
        isError: hydrateMutation.isError,
      })
    ) {
      hydrateMutation.mutate();
    }
  }, [
    needsHydration,
    hydrateMutation.isPending,
    hydrateMutation.isSuccess,
    hydrateMutation.isError,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  // Don't render if hydration is not needed or already succeeded.
  if (!needsHydration && !hydrateMutation.isPending) return null;
  // Both "hydrated" and "no_cloud_data" are successful 200 responses — dismiss.
  if (hydrateMutation.isSuccess) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-background/95 backdrop-blur-sm">
      <div className="flex max-w-sm flex-col items-center px-6 text-center">
        {hydrateMutation.isError ? (
          <>
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-destructive/10">
              <AlertCircle className="h-7 w-7 text-destructive" />
            </div>
            {hydrationFailureKind(hydrateMutation.error) === 'reconnect' ? (
              <>
                <h2 className="mt-5 text-[18px] font-semibold text-foreground">
                  Reconnect Your Storage
                </h2>
                <p className="mt-2 text-[14px] leading-relaxed text-muted-foreground">
                  We can no longer read your backup — the file may have moved, or
                  access may have been revoked. Reconnect your cloud storage to
                  restore your data. Nothing has been deleted.
                </p>
                <a
                  href="/settings/storage"
                  className="mt-5 inline-flex items-center gap-2 rounded-md bg-primary px-5 py-2.5 text-[14px] font-medium text-primary-foreground transition-colors hover:bg-primary/90"
                >
                  Go to Storage Settings
                </a>
              </>
            ) : (
              <>
                <h2 className="mt-5 text-[18px] font-semibold text-foreground">
                  Restore Failed
                </h2>
                <p className="mt-2 text-[14px] leading-relaxed text-muted-foreground">
                  {hydrateMutation.error instanceof Error
                    ? hydrateMutation.error.message
                    : 'Could not restore your data from the cloud. Please try again.'}
                </p>
                <button
                  onClick={() => hydrateMutation.mutate()}
                  className="mt-5 inline-flex items-center gap-2 rounded-md bg-primary px-5 py-2.5 text-[14px] font-medium text-primary-foreground transition-colors hover:bg-primary/90"
                >
                  <RefreshCw className="h-4 w-4" />
                  Retry
                </button>
              </>
            )}
          </>
        ) : (
          <>
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10">
              <CloudDownload className="h-7 w-7 text-primary" />
            </div>
            <h2 className="mt-5 text-[18px] font-semibold text-foreground">
              Restoring Your Data
            </h2>
            <p className="mt-2 text-[14px] leading-relaxed text-muted-foreground">
              Downloading and decrypting your data from the cloud.
              This may take a moment.
            </p>
            <div className="mt-5">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
